import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Smartphone,
  Key,
  History,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Copy,
  RefreshCw,
  Globe,
  Clock,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecuritySettings() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  
  // 2FA Setup state
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [setupStep, setSetupStep] = useState('qr'); // qr, verify, backup
  
  // 2FA Disable state
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  
  // Regenerate codes state
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [overviewRes, historyRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/security/overview`, { credentials: 'include' }),
        fetch(`${API_URL}/api/security/login-history?limit=20`, { credentials: 'include' }),
        fetch(`${API_URL}/api/security/audit-logs?limit=20`, { credentials: 'include' })
      ]);

      if (overviewRes.ok) {
        setOverview(await overviewRes.json());
      }
      if (historyRes.ok) {
        const data = await historyRes.json();
        setLoginHistory(data.login_history || []);
      }
      if (logsRes.ok) {
        const data = await logsRes.json();
        setAuditLogs(data.audit_logs || []);
      }
    } catch (error) {
      console.error('Failed to load security data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function startSetup2FA() {
    try {
      setProcessing(true);
      const response = await fetch(`${API_URL}/api/security/2fa/setup`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSetupData(data);
        setSetupStep('qr');
        setShowSetupModal(true);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to start 2FA setup');
      }
    } catch (error) {
      toast.error('Failed to start 2FA setup');
    } finally {
      setProcessing(false);
    }
  }

  async function enable2FA() {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      setProcessing(true);
      const response = await fetch(`${API_URL}/api/security/2fa/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: verificationCode })
      });

      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backup_codes || []);
        setSetupStep('backup');
        toast.success('2FA enabled successfully!');
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Invalid verification code');
      }
    } catch (error) {
      toast.error('Failed to enable 2FA');
    } finally {
      setProcessing(false);
    }
  }

  async function disable2FA() {
    if (!disableCode) {
      toast.error('Please enter your verification code');
      return;
    }

    try {
      setProcessing(true);
      const response = await fetch(`${API_URL}/api/security/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: disableCode })
      });

      if (response.ok) {
        toast.success('2FA disabled');
        setShowDisableModal(false);
        setDisableCode('');
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Invalid verification code');
      }
    } catch (error) {
      toast.error('Failed to disable 2FA');
    } finally {
      setProcessing(false);
    }
  }

  async function regenerateBackupCodes() {
    if (!regenerateCode) {
      toast.error('Please enter your verification code');
      return;
    }

    try {
      setProcessing(true);
      const response = await fetch(`${API_URL}/api/security/2fa/regenerate-backup-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: regenerateCode })
      });

      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backup_codes || []);
        setShowRegenerateModal(false);
        setRegenerateCode('');
        setShowBackupCodes(true);
        toast.success('New backup codes generated!');
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Invalid verification code');
      }
    } catch (error) {
      toast.error('Failed to regenerate codes');
    } finally {
      setProcessing(false);
    }
  }

  function copyBackupCodes() {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied to clipboard');
  }

  function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-700 rounded w-1/4"></div>
          <div className="h-64 bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  const twoFAEnabled = overview?.two_factor_auth?.enabled;

  return (
    <div className="p-6 space-y-6" data-testid="security-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-400" />
          Security Settings
        </h1>
        <p className="text-slate-400 mt-1">
          Manage your account security, two-factor authentication, and review activity
        </p>
      </div>

      {/* Security Recommendations */}
      {overview?.recommendations?.length > 0 && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="py-4">
            <h3 className="font-semibold text-amber-300 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Security Recommendations
            </h3>
            <div className="space-y-2">
              {overview.recommendations.map((rec, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <span className="text-slate-300">{rec.message}</span>
                  {rec.action === 'enable_2fa' && (
                    <Button
                      size="sm"
                      onClick={startSetup2FA}
                      className="bg-indigo-500 hover:bg-indigo-600 text-white"
                    >
                      Enable Now
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two-Factor Authentication */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-indigo-400" />
            Two-Factor Authentication (2FA)
          </CardTitle>
          <CardDescription className="text-slate-400">
            Add an extra layer of security using an authenticator app
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
            <div className="flex items-center gap-4">
              {twoFAEnabled ? (
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <ShieldCheck className="w-6 h-6 text-green-400" />
                </div>
              ) : (
                <div className="p-3 bg-slate-700 rounded-lg">
                  <ShieldAlert className="w-6 h-6 text-slate-400" />
                </div>
              )}
              <div>
                <h4 className="font-semibold text-white">
                  {twoFAEnabled ? '2FA Enabled' : '2FA Not Enabled'}
                </h4>
                <p className="text-sm text-slate-400">
                  {twoFAEnabled
                    ? `Enabled on ${formatDate(overview?.two_factor_auth?.enabled_at)}`
                    : 'Protect your account with an authenticator app'}
                </p>
              </div>
            </div>
            
            {twoFAEnabled ? (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-slate-700 text-slate-300">
                  {overview?.two_factor_auth?.backup_codes_remaining || 0} backup codes
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRegenerateModal(true)}
                  className="border-slate-600 text-slate-300"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Regenerate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDisableModal(true)}
                  className="border-red-500 text-red-400 hover:bg-red-500/20"
                >
                  Disable
                </Button>
              </div>
            ) : (
              <Button
                onClick={startSetup2FA}
                disabled={processing}
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                {processing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Enable 2FA
              </Button>
            )}
          </div>

          {/* Backup Codes Display */}
          {showBackupCodes && backupCodes.length > 0 && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-amber-300">Your Backup Codes</h4>
                <Button variant="ghost" size="sm" onClick={copyBackupCodes} className="text-amber-400">
                  <Copy className="w-4 h-4 mr-1" />
                  Copy All
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, i) => (
                  <code key={i} className="bg-slate-800 text-amber-300 p-2 rounded text-center font-mono">
                    {code}
                  </code>
                ))}
              </div>
              <p className="text-xs text-amber-200/70 mt-3">
                Store these codes safely. Each can only be used once.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBackupCodes(false)}
                className="mt-2 text-slate-400"
              >
                Hide Codes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Login Activity */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-400" />
            Login Activity
          </CardTitle>
          <CardDescription className="text-slate-400">
            Recent login attempts to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-slate-900/50 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-400">
                {overview?.login_activity?.successful_logins_7d || 0}
              </p>
              <p className="text-xs text-slate-400">Successful (7d)</p>
            </div>
            <div className="p-4 bg-slate-900/50 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-400">
                {overview?.login_activity?.failed_attempts_7d || 0}
              </p>
              <p className="text-xs text-slate-400">Failed (7d)</p>
            </div>
            <div className="p-4 bg-slate-900/50 rounded-lg text-center">
              <p className="text-2xl font-bold text-indigo-400">
                {overview?.login_activity?.unique_ips_7d || 0}
              </p>
              <p className="text-xs text-slate-400">Unique IPs (7d)</p>
            </div>
          </div>

          {/* Login History Table */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {loginHistory.map((login, i) => (
              <div
                key={i}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  login.success ? 'bg-slate-900/50' : 'bg-red-500/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  {login.success ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400" />
                  )}
                  <div>
                    <p className="text-sm text-white">{login.success ? 'Successful login' : 'Failed attempt'}</p>
                    <p className="text-xs text-slate-400">
                      <Globe className="w-3 h-3 inline mr-1" />
                      {login.ip_masked || login.ip_address}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {formatDate(login.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Audit Log */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Eye className="w-5 h-5 text-indigo-400" />
            Recent Activity
          </CardTitle>
          <CardDescription className="text-slate-400">
            Security-related actions on your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {auditLogs.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No recent activity</p>
            ) : (
              auditLogs.map((log, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <p className="text-sm text-white">{log.action.replace(/\./g, ' › ')}</p>
                    {log.resource_type && (
                      <p className="text-xs text-slate-400">{log.resource_type}</p>
                    )}
                  </div>
                  <p className="text-xs text-slate-400">{formatDate(log.timestamp)}</p>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* 2FA Setup Modal */}
      <Dialog open={showSetupModal} onOpenChange={setShowSetupModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-indigo-400" />
              {setupStep === 'backup' ? 'Save Your Backup Codes' : 'Set Up Two-Factor Authentication'}
            </DialogTitle>
          </DialogHeader>

          {setupStep === 'qr' && setupData && (
            <div className="space-y-4 py-4">
              <p className="text-slate-400 text-sm">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>
              
              {/* QR Code placeholder - in real app would render actual QR */}
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-lg">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.provisioning_uri)}`}
                    alt="2FA QR Code"
                    className="w-48 h-48"
                  />
                </div>
              </div>

              <div className="text-center">
                <p className="text-xs text-slate-500 mb-2">Or enter this code manually:</p>
                <code className="bg-slate-800 text-indigo-400 px-3 py-2 rounded text-sm">
                  {setupData.secret}
                </code>
              </div>

              <Button
                onClick={() => setSetupStep('verify')}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                I've Scanned the Code
              </Button>
            </div>
          )}

          {setupStep === 'verify' && (
            <div className="space-y-4 py-4">
              <p className="text-slate-400 text-sm">
                Enter the 6-digit code from your authenticator app to verify setup
              </p>
              
              <div>
                <Label className="text-slate-300">Verification Code</Label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="bg-slate-800 border-slate-700 text-white text-center text-2xl tracking-widest mt-1"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setSetupStep('qr')}
                  className="flex-1 border-slate-600 text-slate-300"
                >
                  Back
                </Button>
                <Button
                  onClick={enable2FA}
                  disabled={processing || verificationCode.length !== 6}
                  className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white"
                >
                  {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Verify & Enable'}
                </Button>
              </div>
            </div>
          )}

          {setupStep === 'backup' && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-300 font-semibold mb-2">⚠️ Save these backup codes!</p>
                <p className="text-amber-200/70 text-sm">
                  If you lose access to your authenticator app, you can use these codes to sign in.
                  Each code can only be used once.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, i) => (
                  <code key={i} className="bg-slate-800 text-green-400 p-2 rounded text-center font-mono text-sm">
                    {code}
                  </code>
                ))}
              </div>

              <Button
                variant="outline"
                onClick={copyBackupCodes}
                className="w-full border-slate-600 text-slate-300"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy All Codes
              </Button>

              <Button
                onClick={() => {
                  setShowSetupModal(false);
                  setSetupStep('qr');
                  setVerificationCode('');
                  setSetupData(null);
                }}
                className="w-full bg-green-500 hover:bg-green-600 text-white"
              >
                I've Saved My Codes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Modal */}
      <Dialog open={showDisableModal} onOpenChange={setShowDisableModal}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-400" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will make your account less secure. Enter your 2FA code to confirm.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label className="text-slate-300">Verification Code</Label>
            <Input
              type="text"
              maxLength={6}
              placeholder="000000"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
              className="bg-slate-800 border-slate-700 text-white mt-1"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDisableModal(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={disable2FA}
              disabled={processing}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Disable 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Backup Codes Modal */}
      <Dialog open={showRegenerateModal} onOpenChange={setShowRegenerateModal}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-indigo-400" />
              Regenerate Backup Codes
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will invalidate your old backup codes. Enter your 2FA code to confirm.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label className="text-slate-300">Verification Code</Label>
            <Input
              type="text"
              maxLength={6}
              placeholder="000000"
              value={regenerateCode}
              onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, ''))}
              className="bg-slate-800 border-slate-700 text-white mt-1"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRegenerateModal(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={regenerateBackupCodes}
              disabled={processing}
              className="bg-indigo-500 hover:bg-indigo-600 text-white"
            >
              {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Regenerate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
